/**
 * RequirementModal.jsx — shared create/edit modal for art_special_requirements (Phase 9, 2026-02).
 *
 * Mirrors the AssetModal pattern:
 *   mode="create"  — POST /art/requirements
 *   mode="edit"    — PATCH /art/requirements/{id}
 *
 * Entry points (via useRequirementActions()):
 *   1. Global "+ Add Requirement" button on the tab header
 *   2. RequirementRow / RequirementCard click → edit
 *   3. ArtByScene drawer roll-up "+ New requirement (this scene)"
 *   4. ArtByLocation block roll-up "+ New requirement (this location)"
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, Trash2, Upload } from 'lucide-react';
import { AuthImage } from '../../components/AuthImage';
import {
  REQ_CATEGORIES, REQ_CATEGORY_TOKENS,
  REQ_PRIORITY_ORDER, REQ_PRIORITY_TOKENS,
  REQ_STATUS_ORDER, REQ_STATUS_TOKENS,
  ReqPriorityPill,
} from './artShared';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: '', category: 'fire', priority: 'standard',
  description: '', linked_scene_ids: [], linked_location_ids: [],
  status: 'needed', assigned_to: '', notes: '',
};

export default function RequirementModal({
  open, mode = 'create', req = null, prefill = null,
  projectId, token, scenes = [], locations = [],
  onClose, onSaved, onDeleted,
}) {
  const [form, setForm] = useState(EMPTY);
  const [photoRows, setPhotoRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  useEffect(() => {
    if (!open) return;
    if (req) {
      setForm({
        title: req.title || '',
        category: req.category || 'fire',
        priority: req.priority || 'standard',
        description: req.description || '',
        linked_scene_ids: req.linked_scene_ids || [],
        linked_location_ids: req.linked_location_ids || [],
        status: req.status || 'needed',
        assigned_to: req.assigned_to || '',
        notes: req.notes || '',
      });
      setPhotoRows(req.photos || []);
    } else {
      setForm({ ...EMPTY, ...(prefill || {}) });
      setPhotoRows([]);
    }
  }, [open, req, prefill]);

  if (!open) return null;

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      let saved;
      if (mode === 'edit' && req?.id) {
        const r = await authAxios.patch(
          `/projects/${projectId}/art/requirements/${req.id}`, form,
        );
        saved = r.data;
      } else {
        const r = await authAxios.post(
          `/projects/${projectId}/art/requirements`, form,
        );
        saved = r.data;
      }
      onSaved?.(saved);
      toast.success(mode === 'edit' ? 'Saved' : 'Requirement created');
      onClose?.();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!req?.id) return;
    if (!window.confirm(`Delete "${req.title}"? This cannot be undone.`)) return;
    try {
      await authAxios.delete(`/projects/${projectId}/art/requirements/${req.id}`);
      onDeleted?.(req);
      toast.success('Deleted');
      onClose?.();
    } catch (e) { console.error(e); toast.error('Delete failed'); }
  };

  const uploadPhoto = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (mode !== 'edit' || !req?.id) {
      toast.info('Save the requirement first, then add photos.');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authAxios.post(
        `/projects/${projectId}/art/requirements/${req.id}/photos`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setPhotoRows(prev => [...prev, { file_id: r.data.file_id, storage_path: r.data.storage_path }]);
      toast.success('Photo added');
    } catch (err) { console.error(err); toast.error('Upload failed'); }
  };
  const removePhoto = async (fid) => {
    if (!req?.id) return;
    try {
      await authAxios.delete(`/projects/${projectId}/art/requirements/${req.id}/photos/${fid}`);
      setPhotoRows(prev => prev.filter(x => x.file_id !== fid));
    } catch (e) { console.error(e); toast.error('Remove failed'); }
  };

  const toggleScene = (sid) => setForm(f => ({
    ...f,
    linked_scene_ids: f.linked_scene_ids.includes(sid)
      ? f.linked_scene_ids.filter(x => x !== sid)
      : [...f.linked_scene_ids, sid],
  }));
  const toggleLoc = (lid) => setForm(f => ({
    ...f,
    linked_location_ids: f.linked_location_ids.includes(lid)
      ? f.linked_location_ids.filter(x => x !== lid)
      : [...f.linked_location_ids, lid],
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="requirement-modal">
      <div className="w-[720px] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg">
        <header className="sticky top-0 flex items-center gap-2 px-4 py-3 bg-[var(--color-bg)] border-b border-[var(--color-border)]">
          <span className="text-sm font-bold text-[var(--color-accent)]">
            {mode === 'edit' ? 'Edit requirement' : 'Add special requirement'}
          </span>
          <ReqPriorityPill priority={form.priority} className="ml-1" />
          <button onClick={onClose} className="ml-auto text-[var(--color-text-dim)] hover:text-white" data-testid="requirement-modal-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Title *</div>
            <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm text-[var(--color-text)]"
              placeholder="e.g. Practical working fireplace"
              data-testid="requirement-modal-title" />
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Category *</div>
            <div className="flex gap-1 flex-wrap">
              {REQ_CATEGORIES.map(k => (
                <button key={k} type="button"
                  onClick={() => setForm(f => ({ ...f, category: k }))}
                  className={`text-[11px] px-2 py-1 rounded-full border ${form.category === k ? 'border-[var(--color-accent)] bg-[var(--color-panel)]' : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]'}`}
                  style={{ color: form.category === k ? REQ_CATEGORY_TOKENS[k].fg : '#a0a0a0' }}
                  data-testid={`requirement-modal-cat-${k}`}>
                  {REQ_CATEGORY_TOKENS[k].label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Priority</div>
            <div className="inline-flex gap-0 border border-[var(--color-border)] rounded overflow-hidden">
              {REQ_PRIORITY_ORDER.map(p => (
                <button key={p} type="button"
                  onClick={() => setForm(f => ({ ...f, priority: p }))}
                  className="text-[11px] px-3 py-1"
                  style={{
                    background: form.priority === p ? REQ_PRIORITY_TOKENS[p].bg : 'transparent',
                    color: form.priority === p ? REQ_PRIORITY_TOKENS[p].fg : '#888',
                    fontWeight: form.priority === p ? 700 : 400,
                  }}
                  data-testid={`requirement-modal-pri-${p}`}>
                  {REQ_PRIORITY_TOKENS[p].label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Description</div>
            <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm text-[var(--color-text)] min-h-[70px]"
              data-testid="requirement-modal-description" />
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1 flex items-center gap-2">
              <span>Photos</span>
              {mode === 'edit' && (
                <button type="button" onClick={uploadPhoto}
                  className="ml-auto text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
                  data-testid="requirement-modal-upload-photo">
                  <Upload className="w-3 h-3 inline mr-1" /> Add photo
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            </div>
            {photoRows.length === 0 ? (
              <div className="text-[11px] italic text-[var(--color-text-mute)]">
                {mode === 'edit' ? 'No photos yet.' : 'Save the requirement first to attach photos.'}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-1">
                {photoRows.map(r => (
                  <div key={r.file_id} className="relative group aspect-square rounded overflow-hidden border border-[var(--color-border)]">
                    <AuthImage path={r.storage_path} className="w-full h-full object-cover" alt="" />
                    <button type="button" onClick={() => removePhoto(r.file_id)}
                      className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 bg-black/70 text-[var(--color-priority-critical)] rounded p-0.5"
                      title="Remove photo">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">
              Linked scenes ({form.linked_scene_ids.length})
            </div>
            <div className="flex gap-1 flex-wrap max-h-24 overflow-y-auto">
              {scenes.map(s => (
                <button key={s.id} type="button" onClick={() => toggleScene(s.id)}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${form.linked_scene_ids.includes(s.id) ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/20 text-[var(--color-accent)]' : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:border-[var(--color-border-strong)]'}`}
                  data-testid={`requirement-modal-scene-${s.scene_number}`}>
                  #{s.scene_number}
                </button>
              ))}
              {scenes.length === 0 && <span className="text-[11px] italic text-[var(--color-text-mute)]">No scenes loaded.</span>}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">
              Linked locations ({form.linked_location_ids.length})
            </div>
            <div className="flex gap-1 flex-wrap max-h-24 overflow-y-auto">
              {locations.map(l => (
                <button key={l.name} type="button"
                  onClick={() => l.id && toggleLoc(l.id)}
                  disabled={!l.id}
                  title={!l.id ? 'This location needs an address / write to promote before linking.' : ''}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${form.linked_location_ids.includes(l.id) ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/20 text-[var(--color-accent)]' : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:border-[var(--color-border-strong)]'} ${!l.id ? 'opacity-40 cursor-not-allowed' : ''}`}
                  data-testid={`requirement-modal-loc-${l.name.toLowerCase().replace(/\s+/g,'-')}`}>
                  {l.name}
                </button>
              ))}
              {locations.length === 0 && <span className="text-[11px] italic text-[var(--color-text-mute)]">No locations loaded.</span>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Status</div>
              <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-sm text-[var(--color-text)]"
                data-testid="requirement-modal-status">
                {REQ_STATUS_ORDER.map(s => (
                  <option key={s} value={s}>{REQ_STATUS_TOKENS[s].label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Assigned to</div>
              <input value={form.assigned_to} onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-sm text-[var(--color-text)]"
                placeholder="Name / dept / vendor"
                data-testid="requirement-modal-assigned-to" />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Notes</div>
            <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm text-[var(--color-text)] min-h-[60px]"
              placeholder="Additional coordination notes, safety flags, permits…"
              data-testid="requirement-modal-notes" />
          </div>
        </div>

        <footer className="sticky bottom-0 flex items-center gap-2 px-4 py-3 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
          {mode === 'edit' && (
            <button type="button" onClick={doDelete}
              className="text-xs px-3 py-1 rounded bg-transparent border border-[var(--color-priority-critical)] text-[var(--color-priority-critical)] hover:bg-[var(--color-priority-critical)]/10"
              data-testid="requirement-modal-delete">
              <Trash2 className="w-3 h-3 inline mr-1" /> Delete
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose}
              className="text-xs px-3 py-1 rounded bg-[var(--color-panel)] text-[var(--color-text-sec)]"
              data-testid="requirement-modal-cancel">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saving}
              className="text-xs px-3 py-1 rounded bg-[var(--color-accent)] text-black font-bold disabled:opacity-50"
              data-testid="requirement-modal-save">
              {saving ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Create requirement')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
